import { IconChevronDown } from "@tabler/icons-react"
import { type FC, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router"

import { type Milestone } from "@forento/core/models/milestone"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import { query } from "~/utilities/trpc"

import MilestoneCard from "./MilestoneCard"
import ShareMilestoneModal from "./ShareMilestoneModal"

const minCardWidth = 150
const cardGap = 12

type Props = { milestones: Milestone[] }

const Milestones: FC<Props> = ({ milestones }) => {
	const utilities = query.useUtils()
	const markAllSeen = query.milestone.markAllSeen.useMutation({
		async onSuccess() {
			await utilities.milestone.list.invalidate()
		},
	})

	const gridRef = useRef<HTMLDivElement>(null)
	const [columnCount, setColumnCount] = useState(1)
	const [isExpanded, setExpanded] = useState(false)

	const hasMilestones = milestones.length > 0
	useEffect(() => {
		const grid = gridRef.current
		if (grid === null) return

		const observer = new ResizeObserver(([entry]) => {
			const width = entry?.contentRect.width ?? 0
			setColumnCount(Math.max(1, Math.floor((width + cardGap) / (minCardWidth + cardGap))))
		})
		observer.observe(grid)
		return () => observer.disconnect()
	}, [hasMilestones])

	const [selected, setSelected] = useState<{ milestones: Milestone[]; index: number; isNew: boolean } | null>(null)

	// Milestone emails link to the dashboard with ?milestone=<id> to open that milestone directly
	const [searchParams, setSearchParams] = useSearchParams()
	const linkedMilestoneId = Number(searchParams.get("milestone"))

	useEffect(() => {
		const unseen = milestones.filter(milestone => !milestone.isSeen)
		const linked = milestones.find(milestone => milestone.id === linkedMilestoneId)

		if (linked !== undefined) {
			const list = linked.isSeen ? milestones : unseen
			setSelected({ milestones: list, index: list.indexOf(linked), isNew: !linked.isSeen })
			setSearchParams(
				params => {
					params.delete("milestone")
					return params
				},
				{ replace: true },
			)
		} else if (unseen.length > 0) {
			setSelected(current => current ?? { milestones: unseen, index: 0, isNew: true })
		}
	}, [milestones, linkedMilestoneId, setSearchParams])

	function handleClose() {
		if (selected?.isNew) markAllSeen.mutate()
		setSelected(null)
	}

	if (milestones.length === 0) return null

	return (
		<section className="flex flex-col gap-4 rounded-3xl bg-[#fff0dd] p-3 sm:p-4">
			<ShareMilestoneModal
				milestones={selected?.milestones ?? []}
				index={selected?.index ?? 0}
				isNew={selected?.isNew ?? false}
				onIndexChange={index => setSelected(current => current && { ...current, index })}
				onClose={handleClose}
			/>
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="min-w-0">
					<p className="text-primary text-sm font-semibold">Milestones</p>
					<h2 className="text-xl font-semibold text-black">Celebrate your wins</h2>
					<p className="mt-1 text-sm leading-5 text-gray-600">
						Share your milestones and launches with your audience on social media.
					</p>
				</div>
				{milestones.length > columnCount && (
					<Button
						className="text-primary shrink-0 items-center gap-1 text-sm font-semibold"
						onClick={() => setExpanded(current => !current)}
					>
						{isExpanded ? "Show less" : `Show all ${milestones.length}`}
						<IconChevronDown className={cn("size-4 transition-transform", isExpanded && "rotate-180")} />
					</Button>
				)}
			</div>
			<div
				ref={gridRef}
				className="grid gap-3"
				style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
			>
				{(isExpanded ? milestones : milestones.slice(0, columnCount)).map(milestone => (
					<button
						key={milestone.id}
						type="button"
						onClick={() => setSelected({ milestones, index: milestones.indexOf(milestone), isNew: false })}
						className="focus-visible:outline-primary cursor-pointer overflow-hidden rounded-2xl transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2"
						aria-label="Share milestone"
					>
						<MilestoneCard milestone={milestone} format="square" />
					</button>
				))}
			</div>
		</section>
	)
}

export default Milestones
